import { useState, useCallback, useEffect } from "react";
import { useSessionStorage } from "usehooks-ts";
import { RetrieveConfigurationResult } from "../workers/pmui";
import { useDebugMode } from "./useDebugMode";
import { CC2MasterSheetDataRow } from "../pages/pm-grading/types";

/**
 * Custom hook for managing PM Grading configuration data (simplified)
 */
export const usePMGradingConfig = (
  pmuiWorker: typeof import("../workers/pmui"),
  authToken: string | undefined,
  entityId: string | undefined
) => {
  const debugMode = useDebugMode();

  // Configuration storage
  const [pmuiConfiguration, setPMUIConfiguration] =
    useSessionStorage<RetrieveConfigurationResult>(
      "ccPMUIConfiguration",
      {} as RetrieveConfigurationResult
    );
  const [pmuiConfigurationLastUpdated, setPMUIConfigurationLastUpdated] =
    useSessionStorage<number | null>("ccPMUIConfigurationLastUpdated", null);

  const [
    latestConfigurationRetrievalInProgress,
    setLatestConfigurationRetrievalInProgress,
  ] = useState(false);

  const [entityIdCC2, setEntityIdCC2] = useSessionStorage<
    CC2MasterSheetDataRow | undefined
  >(`ccPMUIEntityIdCC2_${entityId}`, undefined);

  // All options state
  const [options, setOptions] = useState({
    packagingDescriptionOptions: [] as string[],
    disposalReasonOptions: [] as string[],
    engineers: [] as string[],
    faultCodeOptions: [] as string[],
    gradeOptions: [] as string[],
    returnReasonOptions: [] as string[],
    clientCodeOptions: [] as string[],
  });

  // Update entityIdCC2 whenever entityId or pmuiConfiguration changes
  useEffect(() => {
    const cc2Data = pmuiConfiguration?.cc2Data || [];
    if (entityId && Array.isArray(cc2Data)) {
      const entityIdPrefix = entityId.substring(0, entityId.lastIndexOf("-"));
      const entityCC2 = cc2Data.find(
        (item) => item.skuSuffix === entityIdPrefix
      );
      setEntityIdCC2(entityCC2);
    }
  }, [entityId, pmuiConfiguration, setEntityIdCC2]);

  useEffect(() => {
    setOptions(extractOptions(pmuiConfiguration, entityIdCC2));
  }, [pmuiConfiguration, entityIdCC2]);

  // Helper: extract all options from config
  const extractOptions = (
    config: RetrieveConfigurationResult,
    entityIdCC2: CC2MasterSheetDataRow | undefined
  ) => {
    const configuration = config?.configuration || {};
    const cc2Data = config?.cc2Data || [];
    // Engineers
    const engineers = Array.isArray(configuration.engineers)
      ? configuration.engineers.filter((x) => x && x.trim().length > 0)
      : [];
    // Packaging
    const packagingDescriptionOptions = Array.isArray(
      configuration.descriptionOfPackaging
    )
      ? configuration.descriptionOfPackaging.filter(
          (x) => x && x.trim().length > 0
        )
      : [];
    // Disposal
    const disposalReasonOptions = Array.isArray(
      configuration.disposalIncompleteReason
    )
      ? configuration.disposalIncompleteReason.filter(
          (x) => x && x.trim().length > 0
        )
      : [];
    // Grades
    const gradeOptions = Array.isArray(configuration.grades)
      ? configuration.grades.filter((x) => x)
      : [];
    // Fault codes
    let faultCodeOptions: string[] = [];
    if (Array.isArray(configuration.faultCodes)) {
      if (entityIdCC2?.client) {
        const clientFaultCodes = configuration.faultCodes.find(
          (i) => i && i.clientCode === entityIdCC2.client
        );
        if (clientFaultCodes && Array.isArray(clientFaultCodes.reasons)) {
          faultCodeOptions = clientFaultCodes.reasons.filter(
            (x) => x && x.trim().length > 0
          );
        }
      } else {
        faultCodeOptions = Array.from(
          new Set(
            configuration.faultCodes
              .filter((fc) => fc)
              .flatMap((fc) =>
                Array.isArray(fc.reasons)
                  ? fc.reasons.filter((r) => r && r.trim().length > 0)
                  : []
              )
          )
        );
      }
    }

    // Return reasons
    let returnReasonOptions: string[] = [];
    if (Array.isArray(configuration.reasonsForReturn)) {
      if (entityIdCC2?.client) {
        const clientReturnReasons = configuration.reasonsForReturn.find(
          (i) => i && i.pmSheetId === entityIdCC2.pmSpreadsheetId
        );
        if (clientReturnReasons && Array.isArray(clientReturnReasons.reasons)) {
          returnReasonOptions = clientReturnReasons.reasons.filter(
            (x) => x && x.trim().length > 0
          );
        } else {
          returnReasonOptions = Array.from(
            new Set(
              configuration.reasonsForReturn
                .filter((rr) => rr)
                .flatMap((rr) =>
                  Array.isArray(rr.reasons)
                    ? rr.reasons.filter((r) => r && r.trim().length > 0)
                    : []
                )
            )
          );
        }
      }
    }
    // Client code options
    let clientCodeOptions: string[] = [];
    if (entityIdCC2) {
      clientCodeOptions = cc2Data
        .filter((cc2) => cc2.pmSpreadsheetId === entityIdCC2.pmSpreadsheetId)
        .map((cc2) => cc2.client);
    }
    return {
      packagingDescriptionOptions,
      disposalReasonOptions,
      engineers,
      faultCodeOptions,
      gradeOptions,
      returnReasonOptions,
      clientCodeOptions,
    };
  };

  // Main loader function
  const loadConfiguration = useCallback(
    async (forceRefresh = false) => {
      if (!authToken || latestConfigurationRetrievalInProgress) return;
      const configAge = pmuiConfigurationLastUpdated
        ? Date.now() - pmuiConfigurationLastUpdated
        : Infinity;
      const isRecent = configAge < 60 * 60 * 1000; // 1 hour
      const hasConfig =
        pmuiConfiguration &&
        pmuiConfiguration.configuration &&
        Object.keys(pmuiConfiguration.configuration).length > 0;
      if (hasConfig && isRecent && !forceRefresh) {
        setOptions(extractOptions(pmuiConfiguration, entityIdCC2));
        return;
      }
      setLatestConfigurationRetrievalInProgress(true);
      try {
        const configResult = await pmuiWorker.retrieveConfiguration(authToken);
        if (configResult) {
          setPMUIConfiguration(configResult);
          setPMUIConfigurationLastUpdated(Date.now());
        }
      } catch (error) {
        setPMUIConfiguration({} as RetrieveConfigurationResult);
        if (debugMode) {
          console.error(
            "[usePMGradingConfig] Error retrieving configuration:",
            error
          );
        }
      } finally {
        setTimeout(() => {
          setLatestConfigurationRetrievalInProgress(false);
        }, 2000); // Delay to prevent rapid re-fetching in case of failure
      }
    },
    [
      authToken,
      latestConfigurationRetrievalInProgress,
      pmuiConfigurationLastUpdated,
      pmuiConfiguration,
      entityIdCC2,
      pmuiWorker,
      setPMUIConfiguration,
      setPMUIConfigurationLastUpdated,
      debugMode,
    ]
  );

  // Load on mount and when authToken/entityId/entityIdCC2 changes
  useEffect(() => {
    if (authToken) {
      loadConfiguration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, entityId]);

  // Expose force fetch
  const forceFetchLatestConfiguration = () => loadConfiguration(true);

  return {
    pmuiConfiguration,
    latestConfigurationRetrievalInProgress,
    ...options,
    fetchLatestConfiguration: loadConfiguration,
    forceFetchLatestConfiguration,
  };
};
